import { c as _c } from "react/compiler-runtime";
// «Новая заявка»: бизнес, найденный менеджером (звонок, выставка, холодная
// база). Пока вводишь телефон, почту или ИНН — подсказываем похожие заявки,
// чтобы один бизнес не вели два менеджера. Шлюз дубль всё равно не пропустит
// (409) — тогда показываем, с чем совпало, и даём «Всё равно завести».
import { CRM_LEAD_SOURCES, CRM_LEAD_SOURCE_LABELS, type CrmDuplicate, type CrmLeadCreate, type CrmLeadSource } from "@osmix/contracts";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";
import { AlertTriangle } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { api, ApiError, type ApplicationAssignee } from "@/api";
import { useCreateLead, useLeadDuplicates } from "@/api/hooks/use-application-crm";
import { Select } from "@/components/ui/select";
import { errorText, formatDay } from "./format";
import { FieldError, Spinner, Textarea } from "./ui";
const splitList = (value: string) => value.split(/[,;\n]/).map(item => item.trim()).filter(Boolean);

/** Значение через паузу — не дёргать поиск дублей на каждую цифру. */
function useDebounced(value, t0) {
  const $ = _c(4);
  const ms = t0 === undefined ? 450 : t0;
  const [debounced, setDebounced] = useState(value);
  let t1;
  let t2;
  if ($[0] !== ms || $[1] !== value) {
    t1 = () => {
      const timer = setTimeout(() => setDebounced(value), ms);
      return () => clearTimeout(timer);
    };
    t2 = [value, ms];
    $[0] = ms;
    $[1] = value;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return debounced;
}

/** Дубли из 409: шлюз кладёт их в `details`, клиент консоли доносит их в ApiError. */
const duplicatesOf = (error: unknown): CrmDuplicate[] | null => {
  const details = (error as {
    details?: {
      duplicates?: CrmDuplicate[];
    };
  } | null)?.details;
  return Array.isArray(details?.duplicates) ? details.duplicates : null;
};
const MATCHED: Record<CrmDuplicate["matchedBy"][number], string> = {
  phone: "телефон",
  email: "почта",
  inn: "ИНН"
};
function Field(t0) {
  const $ = _c(9);
  const {
    label,
    required,
    children,
    className
  } = t0;
  const t1 = `flex min-w-0 flex-col gap-1 ${className ?? ""}`;
  let t2;
  if ($[0] !== required) {
    t2 = required && <span className="text-destructive"> *</span>;
    $[0] = required;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label || $[3] !== t2) {
    t3 = <span className="text-xs text-muted-foreground">{label}{t2}</span>;
    $[2] = label;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children || $[6] !== t1 || $[7] !== t3) {
    t4 = <label className={t1}>{t3}{children}</label>;
    $[5] = children;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
export function NewLeadDialog(t0) {
  const $ = _c(130);
  const {
    open,
    onOpenChange,
    assignees
  } = t0;
  const navigate = useNavigate();
  const create = useCreateLead();
  const [shopName, setShopName] = useState("");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [inn, setInn] = useState("");
  const [categories, setCategories] = useState("");
  const [source, setSource] = useState("manual");
  const [sourceDetail, setSourceDetail] = useState("");
  const [tags, setTags] = useState("");
  const [comment, setComment] = useState("");
  const [assignee, setAssignee] = useState("");
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(null);
  let t1;
  if ($[0] !== phone) {
    t1 = phone.trim();
    $[0] = phone;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const debouncedPhone = useDebounced(t1);
  let t2;
  if ($[2] !== email) {
    t2 = email.trim();
    $[2] = email;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const debouncedEmail = useDebounced(t2);
  let t3;
  if ($[4] !== inn) {
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = /\s/g;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t3 = inn.replace(t4, "");
    $[4] = inn;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const debouncedInn = useDebounced(t3);
  let t4;
  if ($[7] !== debouncedEmail || $[8] !== debouncedInn || $[9] !== debouncedPhone) {
    t4 = {
      phone: debouncedPhone,
      email: debouncedEmail,
      inn: debouncedInn
    };
    $[7] = debouncedEmail;
    $[8] = debouncedInn;
    $[9] = debouncedPhone;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const hints = useLeadDuplicates(t4);
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setShopName("");
      setContactName("");
      setPhone("");
      setEmail("");
      setCity("");
      setInn("");
      setCategories("");
      setSource("manual");
      setSourceDetail("");
      setTags("");
      setComment("");
      setAssignee("");
      setError(null);
      setConflict(null);
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const reset = t5;
  let t6;
  if ($[12] !== create.isPending || $[13] !== onOpenChange) {
    t6 = next => {
      if (!next && create.isPending) {
        return;
      }
      if (!next) {
        reset();
      }
      onOpenChange(next);
    };
    $[12] = create.isPending;
    $[13] = onOpenChange;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const close = t6;
  let t7;
  if ($[15] !== assignee || $[16] !== categories || $[17] !== city || $[18] !== comment || $[19] !== contactName || $[20] !== create || $[21] !== email || $[22] !== inn || $[23] !== navigate || $[24] !== onOpenChange || $[25] !== phone || $[26] !== shopName || $[27] !== source || $[28] !== sourceDetail || $[29] !== tags) {
    t7 = async allowDuplicate => {
      setError(null);
      if (!shopName.trim() || !contactName.trim()) {
        setError("\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0438 \u043A\u043E\u043D\u0442\u0430\u043A\u0442\u043D\u043E\u0435 \u043B\u0438\u0446\u043E");
        return;
      }
      if (phone.replace(/\D/g, "").length < 10) {
        setError("\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u0442\u0435\u043B\u0435\u0444\u043E\u043D \u2014 \u043F\u043E \u043D\u0435\u043C\u0443 \u043C\u0435\u043D\u0435\u0434\u0436\u0435\u0440 \u0437\u0432\u043E\u043D\u0438\u0442 \u0438 \u0438\u0449\u0443\u0442\u0441\u044F \u0434\u0443\u0431\u043B\u0438");
        return;
      }
      const input = {
        shopName: shopName.trim(),
        contactName: contactName.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        city: city.trim() || undefined,
        inn: inn.replace(/\s/g, "") || undefined,
        categories: splitList(categories),
        source,
        sourceDetail: sourceDetail.trim() || undefined,
        tags: splitList(tags),
        comment: comment.trim() || undefined,
        ...(assignee === "none" ? {
          assigneeId: null
        } : assignee ? {
          assigneeId: assignee
        } : {}),
        ...(allowDuplicate ? {
          allowDuplicate: true
        } : {})
      };
      ;
      try {
        const card = await create.mutateAsync(input);
        reset();
        onOpenChange(false);
        navigate(`/applications/${card.application.id}`);
      } catch (t8) {
        const e = t8;
        if (e instanceof ApiError && e.status === 409) {
          const found = duplicatesOf(e) ?? (await api.applicationCrm.duplicates({
            phone: input.phone,
            email: input.email,
            inn: input.inn
          }).catch(_temp));
          setConflict(found);
          setError(e.message);
          return;
        }
        setError(errorText(e));
      }
    };
    $[15] = assignee;
    $[16] = categories;
    $[17] = city;
    $[18] = comment;
    $[19] = contactName;
    $[20] = create;
    $[21] = email;
    $[22] = inn;
    $[23] = navigate;
    $[24] = onOpenChange;
    $[25] = phone;
    $[26] = shopName;
    $[27] = source;
    $[28] = sourceDetail;
    $[29] = tags;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  const submit = t7;
  let t8;
  if ($[31] !== conflict || $[32] !== hints) {
    t8 = conflict ?? hints.data ?? [];
    $[31] = conflict;
    $[32] = hints;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  const shown = t8;
  let t9;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DialogHeader className="border-b px-6 pt-6 pb-4"><DialogTitle>Новая заявка</DialogTitle><DialogDescription>Бизнес, которого нашли сами: звонок, выставка, рекомендация, база.</DialogDescription></DialogHeader>;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== submit) {
    t10 = event => {
      event.preventDefault();
      submit(false);
    };
    $[35] = submit;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e_0 => setShopName(e_0.target.value);
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== shopName) {
    t12 = <Field label={"\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u043C\u0430\u0433\u0430\u0437\u0438\u043D\u0430 \u0438\u043B\u0438 \u0431\u0440\u0435\u043D\u0434\u0430"} required={true}><Input autoFocus={true} value={shopName} onChange={t11} /></Field>;
    $[38] = shopName;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = e_1 => setContactName(e_1.target.value);
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== contactName) {
    t14 = <Field label={"\u041A\u043E\u043D\u0442\u0430\u043A\u0442\u043D\u043E\u0435 \u043B\u0438\u0446\u043E"} required={true}><Input value={contactName} onChange={t13} /></Field>;
    $[41] = contactName;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = e_2 => setPhone(e_2.target.value);
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== phone) {
    t16 = <Field label={"\u0422\u0435\u043B\u0435\u0444\u043E\u043D"} required={true}><Input type="tel" inputMode="tel" placeholder="+7" value={phone} onChange={t15} /></Field>;
    $[44] = phone;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = e_3 => setEmail(e_3.target.value);
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== email) {
    t18 = <Field label={"\u041F\u043E\u0447\u0442\u0430"}><Input type="email" inputMode="email" value={email} onChange={t17} /></Field>;
    $[47] = email;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = e_4 => setCity(e_4.target.value);
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== city) {
    t20 = <Field label={"\u0413\u043E\u0440\u043E\u0434"}><Input value={city} onChange={t19} /></Field>;
    $[50] = city;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = e_5 => setInn(e_5.target.value);
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== inn) {
    t22 = <Field label={"\u0418\u041D\u041D"}><Input inputMode="numeric" value={inn} onChange={t21} /></Field>;
    $[53] = inn;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== conflict || $[56] !== shown) {
    t23 = shown.length > 0 && <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm sm:col-span-2"><AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" /><div className="min-w-0"><p className="font-medium text-amber-800 dark:text-amber-400">{conflict ? "\u0422\u0430\u043A\u0430\u044F \u0437\u0430\u044F\u0432\u043A\u0430 \u0443\u0436\u0435 \u0435\u0441\u0442\u044C" : "\u041F\u043E\u0445\u043E\u0436\u0438\u0435 \u0437\u0430\u044F\u0432\u043A\u0438"}</p><ul className="mt-1 flex flex-col gap-0.5">{shown.map(_temp2)}</ul></div></div>;
    $[55] = conflict;
    $[56] = shown;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = e_6 => setCategories(e_6.target.value);
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== categories) {
    t25 = <Field label={"\u041A\u0430\u0442\u0435\u0433\u043E\u0440\u0438\u0438"} className="sm:col-span-2"><Input placeholder={"\u0447\u0435\u0440\u0435\u0437 \u0437\u0430\u043F\u044F\u0442\u0443\u044E: \u041A\u0435\u0440\u0430\u043C\u0438\u043A\u0430, \u0414\u0435\u043A\u043E\u0440"} value={categories} onChange={t24} /></Field>;
    $[59] = categories;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  let t27;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = e_7 => setSource(e_7.target.value as CrmLeadSource);
    t27 = CRM_LEAD_SOURCES.filter(_temp3).map(_temp4);
    $[61] = t26;
    $[62] = t27;
  } else {
    t26 = $[61];
    t27 = $[62];
  }
  let t28;
  if ($[63] !== source) {
    t28 = <Field label={"\u0418\u0441\u0442\u043E\u0447\u043D\u0438\u043A"}><Select className="w-full" value={source} onChange={t26}>{t27}</Select></Field>;
    $[63] = source;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = e_8 => setSourceDetail(e_8.target.value);
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== sourceDetail) {
    t30 = <Field label={"\u0423\u0442\u043E\u0447\u043D\u0435\u043D\u0438\u0435 \u0438\u0441\u0442\u043E\u0447\u043D\u0438\u043A\u0430"}><Input placeholder={"\u0432\u044B\u0441\u0442\u0430\u0432\u043A\u0430, \u043A\u0442\u043E \u043F\u043E\u0440\u0435\u043A\u043E\u043C\u0435\u043D\u0434\u043E\u0432\u0430\u043B"} value={sourceDetail} onChange={t29} /></Field>;
    $[66] = sourceDetail;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = e_9 => setTags(e_9.target.value);
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== tags) {
    t32 = <Field label={"\u0422\u0435\u0433\u0438"}><Input placeholder={"\u0447\u0435\u0440\u0435\u0437 \u0437\u0430\u043F\u044F\u0442\u0443\u044E"} value={tags} onChange={t31} /></Field>;
    $[69] = tags;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  let t34;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = e_10 => setAssignee(e_10.target.value);
    t34 = <option value="">Я</option>;
    $[71] = t33;
    $[72] = t34;
  } else {
    t33 = $[71];
    t34 = $[72];
  }
  let t35;
  if ($[73] !== assignees) {
    t35 = assignees.map(_temp5);
    $[73] = assignees;
    $[74] = t35;
  } else {
    t35 = $[74];
  }
  let t36;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <option value="none">Без ответственного</option>;
    $[75] = t36;
  } else {
    t36 = $[75];
  }
  let t37;
  if ($[76] !== assignee || $[77] !== t35) {
    t37 = <Field label={"\u041E\u0442\u0432\u0435\u0442\u0441\u0442\u0432\u0435\u043D\u043D\u044B\u0439"}><Select className="w-full" value={assignee} onChange={t33}>{t34}{t35}{t36}</Select></Field>;
    $[76] = assignee;
    $[77] = t35;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = e_11 => setComment(e_11.target.value);
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] !== comment) {
    t39 = <Field label={"\u041A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0439"} className="sm:col-span-2"><Textarea rows={3} value={comment} onChange={t38} /></Field>;
    $[80] = comment;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  let t40;
  if ($[82] !== t12 || $[83] !== t14 || $[84] !== t16 || $[85] !== t18 || $[86] !== t20 || $[87] !== t22 || $[88] !== t23 || $[89] !== t25 || $[90] !== t28 || $[91] !== t30 || $[92] !== t32 || $[93] !== t37 || $[94] !== t39) {
    t40 = <div className="grid gap-3 sm:grid-cols-2">{t12}{t14}{t16}{t18}{t20}{t22}{t23}{t25}{t28}{t30}{t32}{t37}{t39}</div>;
    $[82] = t12;
    $[83] = t14;
    $[84] = t16;
    $[85] = t18;
    $[86] = t20;
    $[87] = t22;
    $[88] = t23;
    $[89] = t25;
    $[90] = t28;
    $[91] = t30;
    $[92] = t32;
    $[93] = t37;
    $[94] = t39;
    $[95] = t40;
  } else {
    t40 = $[95];
  }
  let t41;
  if ($[96] !== t10 || $[97] !== t40) {
    t41 = <form id="new-lead-form" className="min-h-0 flex-1 overflow-y-auto px-6 py-4" onSubmit={t10}>{t40}</form>;
    $[96] = t10;
    $[97] = t40;
    $[98] = t41;
  } else {
    t41 = $[98];
  }
  let t42;
  if ($[99] !== conflict || $[100] !== error) {
    t42 = !conflict && <FieldError>{error}</FieldError>;
    $[99] = conflict;
    $[100] = error;
    $[101] = t42;
  } else {
    t42 = $[101];
  }
  let t43;
  if ($[102] !== close) {
    t43 = () => close(false);
    $[102] = close;
    $[103] = t43;
  } else {
    t43 = $[103];
  }
  let t44;
  if ($[104] !== create.isPending || $[105] !== t43) {
    t44 = <Button variant="outline" disabled={create.isPending} onClick={t43}>Отмена</Button>;
    $[104] = create.isPending;
    $[105] = t43;
    $[106] = t44;
  } else {
    t44 = $[106];
  }
  let t45;
  if ($[107] !== conflict || $[108] !== create.isPending || $[109] !== submit) {
    t45 = conflict && <Button variant="outline" disabled={create.isPending} onClick={() => void submit(true)}>Всё равно завести</Button>;
    $[107] = conflict;
    $[108] = create.isPending;
    $[109] = submit;
    $[110] = t45;
  } else {
    t45 = $[110];
  }
  let t46;
  if ($[111] !== create.isPending) {
    t46 = create.isPending && <Spinner />;
    $[111] = create.isPending;
    $[112] = t46;
  } else {
    t46 = $[112];
  }
  let t47;
  if ($[113] !== create.isPending || $[114] !== t46) {
    t47 = <Button type="submit" form="new-lead-form" disabled={create.isPending}>{t46}Завести заявку</Button>;
    $[113] = create.isPending;
    $[114] = t46;
    $[115] = t47;
  } else {
    t47 = $[115];
  }
  let t48;
  if ($[116] !== t44 || $[117] !== t45 || $[118] !== t47) {
    t48 = <div className="flex flex-wrap justify-end gap-2">{t44}{t45}{t47}</div>;
    $[116] = t44;
    $[117] = t45;
    $[118] = t47;
    $[119] = t48;
  } else {
    t48 = $[119];
  }
  let t49;
  if ($[120] !== t42 || $[121] !== t48) {
    t49 = <div className="flex flex-col gap-2 border-t px-6 py-4">{t42}{t48}</div>;
    $[120] = t42;
    $[121] = t48;
    $[122] = t49;
  } else {
    t49 = $[122];
  }
  let t50;
  if ($[123] !== t41 || $[124] !== t49) {
    t50 = <DialogContent className="flex max-h-[92dvh] flex-col gap-0 p-0 sm:max-w-2xl">{t9}{t41}{t49}</DialogContent>;
    $[123] = t41;
    $[124] = t49;
    $[125] = t50;
  } else {
    t50 = $[125];
  }
  let t51;
  if ($[126] !== close || $[127] !== open || $[128] !== t50) {
    t51 = <Dialog open={open} onOpenChange={close}>{t50}</Dialog>;
    $[126] = close;
    $[127] = open;
    $[128] = t50;
    $[129] = t51;
  } else {
    t51 = $[129];
  }
  return t51;
}
function _temp5(item) {
  return <option key={item.id} value={item.id}>{item.name}</option>;
}
function _temp4(key_0) {
  return <option key={key_0} value={key_0}>{CRM_LEAD_SOURCE_LABELS[key_0]}</option>;
}
function _temp3(key) {
  return key !== "landing";
}
function _temp2(duplicate) {
  return <DuplicateRow key={duplicate.id} duplicate={duplicate} />;
}
function _temp() {
  return [] as CrmDuplicate[];
}
function DuplicateRow(t0) {
  const $ = _c(19);
  const {
    duplicate
  } = t0;
  const {
    t
  } = useTranslation();
  const t1 = `/applications/${duplicate.id}`;
  let t2;
  if ($[0] !== duplicate.shopName || $[1] !== t1) {
    t2 = <Link to={t1} target="_blank" className="font-medium underline-offset-4 hover:underline">{duplicate.shopName}</Link>;
    $[0] = duplicate.shopName;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = duplicate.contactName;
  const t4 = `applications.status.${duplicate.status}`;
  let t5;
  if ($[3] !== duplicate.status || $[4] !== t || $[5] !== t4) {
    t5 = t(t4, {
      defaultValue: duplicate.status
    });
    $[3] = duplicate.status;
    $[4] = t;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== duplicate.submittedAt) {
    t6 = formatDay(duplicate.submittedAt);
    $[7] = duplicate.submittedAt;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== duplicate.matchedBy) {
    t7 = duplicate.matchedBy.map(_temp6).join(", ");
    $[9] = duplicate.matchedBy;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== duplicate.contactName || $[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    t8 = <span className="text-muted-foreground">{" "}· {t3} · {t5} ·{" "}{t6} · совпали: {t7}</span>;
    $[11] = duplicate.contactName;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t2 || $[17] !== t8) {
    t9 = <li className="text-foreground">{t2}{t8}</li>;
    $[16] = t2;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}
function _temp6(key) {
  return MATCHED[key];
}